import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Search, Check, X, Ban, Trash2, Star, ExternalLink } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ALL = "__all__";
const PLAN_COLOR = { free: "bg-slate-100 text-slate-600", standard: "bg-blue-100 text-blue-700", premium: "bg-sky-100 text-sky-700" };
const STATUS_COLOR = { approved: "bg-green-50 text-green-700", pending: "bg-amber-50 text-amber-700", rejected: "bg-red-50 text-red-700", suspended: "bg-slate-100 text-slate-600" };

export default function AdminInstitutions() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [plan, setPlan] = useState("");
  const [planNames, setPlanNames] = useState(["free", "standard", "premium"]);
  useEffect(() => {
    api.get("/admin/plans").then((r) => setPlanNames([...new Set(["free", ...r.data.map((p) => p.name)])])).catch(() => {});
  }, []);

  const load = useCallback(() => {
    const params = {};
    if (q) params.q = q;
    if (status) params.status = status;
    if (plan) params.plan = plan;
    api.get("/admin/institutions", { params }).then((r) => setItems(r.data.items)).catch(() => {});
  }, [q, status, plan]);

  useEffect(() => { load(); }, [status, plan]);

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div>
      <h1 className="mb-4 font-heading text-2xl font-bold tracking-tight text-slate-900">Institutions</h1>
      <div className="mb-4 flex flex-wrap gap-2">
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex flex-1 gap-2">
          <div className="flex flex-1 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 max-w-xs">
            <Search className="h-4 w-4 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search..." className="border-0 shadow-none focus-visible:ring-0" data-testid="admin-inst-search" />
          </div>
          <Button type="submit" variant="outline">Search</Button>
        </form>
        <Select value={status || ALL} onValueChange={(v) => setStatus(v === ALL ? "" : v)}>
          <SelectTrigger className="w-40 bg-white" data-testid="admin-inst-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>All statuses</SelectItem>{["pending", "approved", "rejected", "suspended"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={plan || ALL} onValueChange={(v) => setPlan(v === ALL ? "" : v)}>
          <SelectTrigger className="w-40 bg-white"><SelectValue placeholder="Plan" /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>All plans</SelectItem>{planNames.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full min-w-[820px] text-left text-sm">
          <thead className="bg-slate-50 text-slate-500">
            <tr><th className="p-3">Institution</th><th className="p-3">Location</th><th className="p-3">Plan</th><th className="p-3">Courses</th><th className="p-3">Status</th><th className="p-3">Actions</th></tr>
          </thead>
          <tbody>
            {items.map((i) => (
              <tr key={i.id} className="border-t border-slate-100 align-middle" data-testid={`admin-inst-row-${i.id}`}>
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    {i.featured && <Star className="h-4 w-4 fill-sky-500 text-sky-500" />}
                    <span className="font-medium text-slate-900">{i.name}</span>
                  </div>
                </td>
                <td className="p-3 text-slate-500">{[i.city, i.country].filter(Boolean).join(", ") || "—"}</td>
                <td className="p-3">
                  <Select value={i.listing_plan} onValueChange={(v) => act(() => api.post(`/admin/institutions/${i.id}/plan`, { plan: v }), "Plan updated")}>
                    <SelectTrigger className={`h-7 w-28 border-0 text-xs font-medium capitalize ${PLAN_COLOR[i.listing_plan] || "bg-amber-50 text-amber-700"}`} data-testid={`inst-plan-${i.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{planNames.map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="p-3 text-slate-600">{i.course_count}</td>
                <td className="p-3"><span className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${STATUS_COLOR[i.listing_status]}`}>{i.listing_status}</span></td>
                <td className="p-3">
                  <div className="flex items-center gap-1">
                    {i.listing_status !== "approved" && <Button size="icon" variant="ghost" title="Approve" onClick={() => act(() => api.post(`/admin/institutions/${i.id}/approve`), "Approved")} data-testid={`approve-inst-${i.id}`}><Check className="h-4 w-4 text-green-600" /></Button>}
                    {i.listing_status !== "rejected" && <Button size="icon" variant="ghost" title="Reject" onClick={() => act(() => api.post(`/admin/institutions/${i.id}/reject`), "Rejected")}><X className="h-4 w-4 text-red-500" /></Button>}
                    <Button size="icon" variant="ghost" title="Suspend" onClick={() => act(() => api.post(`/admin/institutions/${i.id}/suspend`), "Suspended")}><Ban className="h-4 w-4 text-slate-500" /></Button>
                    <Button size="icon" variant="ghost" title={i.featured ? "Unfeature" : "Feature"} onClick={() => act(() => api.post(`/admin/institutions/${i.id}/featured`, { featured: !i.featured }), "Updated")}><Star className={`h-4 w-4 ${i.featured ? "fill-sky-500 text-sky-500" : "text-slate-400"}`} /></Button>
                    {i.slug && <Button size="icon" variant="ghost" title="View" onClick={() => window.open(`/universities/${i.slug}`, "_blank")}><ExternalLink className="h-4 w-4 text-slate-400" /></Button>}
                    <Button size="icon" variant="ghost" title="Delete" onClick={() => window.confirm(`Delete ${i.name}?`) && act(() => api.delete(`/admin/institutions/${i.id}`), "Deleted")} data-testid={`delete-inst-${i.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-400">No institutions found.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
